import { getArrayBuffer } from 'Loader/IndexedDB_Handler.js';
import { BinaryReader } from '../common/BinaryReader.js';
import { SerializerKind } from '../common/Constants.js';
//import Header from '../common/Header.js';
import { findSerializer } from '../common/SerializerFinder.js';
//import legacySerializers from '../serializers/versionMap';
import GridSerializerV2 from 'Loader/serializerV2/geom/GridSerializer';
import GroundSerializerV2 from 'Loader/serializerV2/geom/GroundSerializer';
import RoadsSerializerV2 from 'Loader/serializerV2/geom/RoadsSerializer';
import TreesSerializerV2 from 'Loader/serializerV2/geom/TreesSerializer';
import TargetsSerializerV2 from 'Loader/serializerV2/geom/TargetsSerializer';
import BuildingsSerializerV2 from 'Loader/serializerV2/geom/BuildingsSerializer';
import { buildGeomFromSchema } from 'Loader/serializerV2/geom/GeomBuilder';
import type { RawDataGroup } from '@visualizer/types';
import type Grid from 'Geodata/grid';
import type { GeoObject as GeoObjectLegacy } from '@data/geodata/legacy/GeoObject';
import type { Ground } from '@data/geodata/legacy/Ground';
import type { Roads } from '@data/geodata/legacy/Roads';
import type { Targets } from '@data/geodata/legacy/Targets';

const privateConstructorToken = Symbol();

export class SerializationManager {
    version = '';
    serializerKind: SerializerKind;

    static geomSerializationManager = SerializationManager.#create(SerializerKind.GEOM);
    findSerializer = findSerializer;

    constructor(token: any, kindOfSerializer: SerializerKind) {
        if (token !== privateConstructorToken) {
            throw new Error('Constructor of SerializationManager is private.');
        }
        this.serializerKind = kindOfSerializer;
    }

    static #create(kindOfSerializer: SerializerKind) {
        return new SerializationManager(privateConstructorToken, kindOfSerializer);
    }

    deserializeGeom(indexedKey: string, classType: 'Buildings', legacy?: boolean): Promise<RawDataGroup[]>;
    deserializeGeom(indexedKey: string, classType: 'Trees' | 'Vegetation', legacy?: boolean): Promise<RawDataGroup[]>;
    deserializeGeom<T extends typeof GeoObjectLegacy>(indexedKey: string, classType: T, legacy?: boolean): Promise<InstanceType<T>>;
    deserializeGeom(indexedKey: string, classType: typeof Ground, legacy?: boolean): Promise<Ground>;
    deserializeGeom(indexedKey: string, classType: typeof Roads, legacy?: boolean): Promise<Roads>;
    deserializeGeom(indexedKey: string, classType: typeof Targets, legacy?: boolean): Promise<Targets>;
    deserializeGeom(indexedKey: string, classType: typeof Grid, legacy?: boolean): Promise<Grid>;
    async deserializeGeom(indexedKey: string, classType: any, legacy: boolean = true) {
        // Create the reader
        const buffer = await getArrayBuffer(indexedKey);
        const reader = new BinaryReader(buffer);

        // read first three ints
        //vMajor
        reader.readInt32();
        //vMinor
        reader.readInt32();
        //vRelease
        reader.readInt32();

        return this.schemaBuilder(buffer, classType, legacy);
    }

    private schemaBuilder(buffer: ArrayBuffer, classType: any, legacy: boolean) {
        const typeName = getTypeName(classType);
        let schema: unknown;

        switch (typeName) {
            case 'Grid':
                schema = new GridSerializerV2().deserialize(buffer);
                break;
            case 'Ground':
                schema = new GroundSerializerV2().deserialize(buffer);
                break;
            case 'Roads':
                schema = new RoadsSerializerV2().deserialize(buffer);
                break;
            case 'Targets':
                schema = new TargetsSerializerV2().deserialize(buffer);
                break;
            case 'Trees':
            case 'Vegetation':
                schema = new TreesSerializerV2().deserialize(buffer);
                break;
            case 'Buildings':
                schema = new BuildingsSerializerV2().deserialize(buffer);
                break;
            default:
                throw new Error(`Unsupported V2 geometry type: ${typeName}`);
        }

        if (!legacy && typeName !== 'Buildings' && typeName !== 'Trees' && typeName !== 'Vegetation') {
            return schema;
        }

        return buildGeomFromSchema(typeName, schema);
    }
}

function getTypeName(actClass: any): string {
    if (typeof actClass === 'string') {
        return actClass;
    }

    if (typeof actClass === 'function' && typeof actClass.name === 'string') {
        return actClass.name;
    }

    if (actClass && typeof actClass === 'object' && 'name' in actClass && typeof actClass.name === 'string') {
        return actClass.name;
    }

    return String(actClass ?? '');
}
